"use client"

import * as React from "react"
import {
  MobileScreenHeader,
  MobileScreenHeaderBackStyle,
  MobileScreenHeaderProvider,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@ibada/ui"

const NO_DEFAULT_BACK = { defaultBack: null }

type MobileDetailScreenProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The record's name, in the top bar. */
  title: string
  backLabel: string
  /** False for a form ("Annuler" as plain text, no chevron). */
  showBackChevron?: boolean
  /** A text action on the right of the top bar (e.g. "Modifier"). */
  action?: { label: string; onPress: () => void; disabled?: boolean }
  /** `MobileFormSection`s of read-only `MobileFormRow`s, usually under a hero. */
  children: React.ReactNode
  /** A form's main action as the app's green pill, pinned under the content (e.g. "Envoyer l'invitation"). */
  submit?: { label: string; onPress: () => void; disabled?: boolean; loading?: boolean }
}

/**
 * A record's details on a phone, as a full screen pushed in from the right (the iOS detail page): a back chevron,
 * the title and an optional action in the top bar, the content scrolling under it on the app canvas. Desktop keeps
 * its own side panel; mount this one only on phones.
 */
export function MobileDetailScreen({
  open,
  onOpenChange,
  title,
  backLabel,
  showBackChevron = true,
  action,
  children,
  submit,
}: MobileDetailScreenProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        showCloseButton={false}
        // Full screen, so the only "outside" is a sheet or menu opened on top of it (a picker, an edit sheet):
        // tapping there must not close this page underneath. It closes with its own back button or Escape.
        onInteractOutside={(event) => event.preventDefault()}
        className='!inset-0 !h-[var(--app-height)] !w-full !max-w-none gap-0 !rounded-none !border-0 bg-[var(--app-canvas)] p-0 shadow-none'
      >
        {/* The shared phone header, not pinned by itself: it sits above this sheet's own scroll area. */}
        <MobileScreenHeader
          className='shrink-0 px-4 pt-[var(--mobile-header-pt)] pb-2'
          sticky={false}
          titleAs={SheetTitle}
          title={title}
          back={{
            label: backLabel,
            onBack: () => onOpenChange(false),
            style: showBackChevron ? MobileScreenHeaderBackStyle.Chevron : MobileScreenHeaderBackStyle.Text,
          }}
          actions={
            action ? (
              <button
                type='button'
                onClick={action.onPress}
                disabled={action.disabled}
                className='inline-flex h-11 items-center rounded-full px-3 text-base font-bold text-primary active:bg-primary/10 disabled:text-muted-foreground/60'
              >
                {action.label}
              </button>
            ) : undefined
          }
        />
        <SheetDescription className='sr-only'>{title}</SheetDescription>
        <div
          className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-2 [&>*]:shrink-0'
          style={{ paddingBottom: "max(1.5rem, calc(env(safe-area-inset-bottom) + 1rem))" }}
        >
          {/* A list or header inside this page must not borrow the back button of the page underneath. */}
          <MobileScreenHeaderProvider value={NO_DEFAULT_BACK}>{children}</MobileScreenHeaderProvider>
        </div>
        {submit ? (
          <div className='shrink-0 px-4 pt-2' style={{ paddingBottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}>
            <button
              type='button'
              onClick={submit.onPress}
              disabled={submit.disabled || submit.loading}
              // Disabled stays green, just faded, like the form screen's pill.
              className='flex h-[3.25rem] w-full items-center justify-center rounded-full bg-primary text-base font-extrabold text-primary-foreground shadow-[0_8px_20px_-8px_rgba(5,150,105,0.6)] active:brightness-95 disabled:opacity-45 disabled:shadow-none'
            >
              {submit.loading ? <span className='size-5 animate-spin rounded-full border-2 border-current border-t-transparent' /> : submit.label}
            </button>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

/** The figure a detail screen is about (a balance, an amount), large and centred under the top bar. */
export function MobileDetailHero({
  eyebrow,
  value,
  badge,
  caption,
  footer,
}: {
  eyebrow?: React.ReactNode
  value: React.ReactNode
  badge?: React.ReactNode
  caption?: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <div className='flex flex-col items-center gap-1.5 py-2 text-center'>
      {eyebrow ? <span className='text-[13px] font-bold text-muted-foreground'>{eyebrow}</span> : null}
      <span className='max-w-full break-words text-[2.5rem] leading-none font-extrabold tracking-tight tabular-nums'>{value}</span>
      {badge}
      {caption ? <p className='max-w-full text-sm font-semibold break-words text-muted-foreground'>{caption}</p> : null}
      {footer ? <div className='mt-1 w-full'>{footer}</div> : null}
    </div>
  )
}

/** A row that is a list item, not a label and value (a linked activity, an allowed category): wraps to two lines. */
export function MobileDetailListRow({ children }: { children: React.ReactNode }) {
  return (
    <div className='flex min-h-[3.25rem] items-center px-4 py-2.5'>
      <span className='line-clamp-2 text-base font-semibold break-words'>{children}</span>
    </div>
  )
}
